import { z } from 'zod';

import type { BillingResponse } from '@/api/generated';
import {
  contactPart,
  contactToPayload,
  emailField,
  optionalText,
} from '@/lib/organization';

/**
 * The billing contact as the Billing page's form holds it, and as the update
 * sends it. Separate from the form so the one rule that matters - the contact
 * is replaced whole - is tested on its own.
 */

export const contactSchema = z.object({
  name: optionalText(),
  title: optionalText(),
  email: emailField(),
  phone: optionalText(),
});

export type ContactInput = z.input<typeof contactSchema>;
export type ContactOutput = z.output<typeof contactSchema>;

type StoredContact = BillingResponse['billing_contact'];

/** The four parts as the form holds them: text, never null. */
export function toContactValues(contact: StoredContact): ContactInput {
  return {
    name: contactPart(contact, 'name'),
    title: contactPart(contact, 'title'),
    email: contactPart(contact, 'email'),
    phone: contactPart(contact, 'phone'),
  };
}

/**
 * What the update sends: the whole contact, or null to clear it.
 *
 * The backend replaces the contact whole rather than merging, so every part the
 * form holds goes every time. It can also hold an address, which this form does
 * not show; that is carried through as it was stored, or saving a new phone
 * number would quietly delete it. Emptying all four parts shown still clears
 * the contact, address and all - which is what "no billing contact" means.
 */
export function toContactPayload(
  values: ContactOutput,
  stored: StoredContact,
): Record<string, string> | null {
  const contact = contactToPayload(values);
  if (!contact) return null;
  const address = contactPart(stored, 'address');
  return address ? { ...contact, address } : contact;
}
